import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Eye,
  ShoppingCart,
  Store,
} from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePermissions } from '@/features/auth';
import { lastNDays } from '@/shared/components/date-range-picker';
import { ErrorState } from '@/shared/components/error-state';
import { StatusBadge } from '@/shared/components/status-badge';
import { reasonLabel } from '@/shared/lib/catalogue-labels';
import { formatNumber } from '@/shared/lib/format';

import {
  useCatalogueHealth,
  useCatalogueHealthItems,
  usePricingHealth,
} from '../api/pricing.queries';
import type { PricingHealth } from '../types/pricing.types';

/** Reasons that hide an item from the storefront; every other reason leaves it shown but not buyable. */
const HIDING_REASONS = new Set([
  'DenominationInactive',
  'BrandInactive',
  'RegionInactive',
  'CountryInactive',
  'NotOffered',
]);

/** What each reason means for the customer, and what clears it. */
const REASON_HELP: Record<string, string> = {
  DenominationInactive:
    'The denomination is switched off. Switch it on in the brand’s denominations.',
  BrandInactive: 'The whole brand is switched off. Activate the brand to show its items again.',
  RegionInactive: 'The store/region these items belong to is switched off. Activate it on Regions.',
  CountryInactive:
    'The country itself is not active yet. Finish Settings & readiness and activate it.',
  NotOffered: 'Not offered in this country. Offer it from the Market page.',
  NoProvider: 'No provider is mapped to supply the code. Map one on the brand’s denominations.',
  ProviderInactive: 'Every mapped provider is switched off. Activate a provider or map another.',
  ProductIdentifierMissing:
    'The provider buys by its own product id (Mintroute EAN, LikeCard product id) and the mapping has none. Set it on the mapping.',
  ProviderCurrencyMissing:
    'The provider’s cost currency is not set, so no price can be calculated.',
  FxMissing: 'An exchange rate the price depends on is missing. See the Exchange rates tab.',
  FxStale: 'An exchange rate is older than the allowed age. See the Exchange rates tab.',
  ProviderBalanceInsufficient:
    'The provider’s prepaid balance is below the item’s cost. Top up the balance on Providers.',
  PricingConfigurationMissing: 'The country has no active pricing parameters. Publish them first.',
  ProviderDeprecated:
    'A mapped provider’s integration is deprecated (HOLYO), so it can never supply. Map another provider.',
};

const PRICING_HEALTH_DAYS = 30;

const shareFormat = new Intl.NumberFormat('en-GB', { style: 'percent', maximumFractionDigits: 1 });
const share = (part: number, whole: number) =>
  whole === 0 ? '—' : shareFormat.format(part / whole);

/**
 * Catalogue health of one country as a funnel — offered, then visible, then
 * purchasable — with why items drop out at each step, and (with
 * reports.pricing.read) how the last 30 days of sales were priced.
 */
export function HealthPanel({ countryId }: { countryId: number }) {
  const health = useCatalogueHealth(countryId);
  const { can } = usePermissions();
  const canPricing = can('reports.pricing.read');
  const [{ from, to }] = useState(() => lastNDays(PRICING_HEALTH_DAYS));
  const pricing = usePricingHealth(countryId, from, to, canPricing);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const toggle = (reason: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(reason)) next.delete(reason);
      else next.add(reason);
      return next;
    });

  if (health.isLoading) return <Skeleton className="h-40" />;
  if (health.error || !health.data)
    return <ErrorState error={health.error} onRetry={() => void health.refetch()} />;

  const { offeredCount, visibleCount, sellableCount, notSellableByReason } = health.data;
  const hidden = offeredCount - visibleCount;
  const shownNotBuyable = visibleCount - sellableCount;
  const blocked = offeredCount - sellableCount;
  const reasons = Object.entries(notSellableByReason).sort((a, b) => b[1] - a[1]);

  return (
    <div className="flex flex-col gap-5 text-xs">
      <p className="text-muted-foreground max-w-3xl">
        Every denomination offered in this country passes through three steps before a customer can
        buy it. Each card counts the items that made it to that step; the difference between cards
        is what got held back, explained in the table below.
      </p>

      <div className="grid grid-cols-1 items-stretch gap-2 md:grid-cols-[1fr_auto_1fr_auto_1fr]">
        <FunnelStep
          icon={<Store className="size-4" aria-hidden="true" />}
          title="Offered"
          value={offeredCount}
          ratio={1}
          explanation="Denominations switched on for this country in the catalogue. The starting point."
          footnote={<span>All offered items</span>}
        />
        <FunnelArrow />
        <FunnelStep
          icon={<Eye className="size-4" aria-hidden="true" />}
          title="Visible"
          value={visibleCount}
          ratio={offeredCount ? visibleCount / offeredCount : 0}
          explanation="Offered items customers can see on the storefront — their brand, region and denomination are all active."
          footnote={
            <>
              <span>{share(visibleCount, offeredCount)} of offered</span>
              {hidden > 0 && (
                <span className="text-status-warning"> · {formatNumber(hidden)} hidden</span>
              )}
            </>
          }
        />
        <FunnelArrow />
        <FunnelStep
          icon={<ShoppingCart className="size-4" aria-hidden="true" />}
          title="Purchasable"
          value={sellableCount}
          ratio={offeredCount ? sellableCount / offeredCount : 0}
          explanation="Visible items a customer can actually check out now — a provider can supply them and a price can be calculated."
          footnote={
            <>
              <span>{share(sellableCount, offeredCount)} of offered</span>
              {shownNotBuyable > 0 && (
                <span className="text-status-warning">
                  {' '}
                  · {formatNumber(shownNotBuyable)} shown but cannot be bought
                </span>
              )}
            </>
          }
        />
      </div>

      <section className="flex flex-col gap-2">
        {blocked === 0 ? (
          <p className="text-status-success flex items-center gap-1.5 font-medium">
            <CheckCircle2 className="size-4" aria-hidden="true" />
            Every offered item is purchasable.
          </p>
        ) : (
          <>
            <div>
              <h3 className="text-sm font-medium">
                Why {formatNumber(blocked)} offered {blocked === 1 ? 'item is' : 'items are'} not
                purchasable
              </h3>
              <p className="text-muted-foreground">
                An item can be held back for more than one reason, so the counts can add up to more
                than {formatNumber(blocked)}. Clear every reason on an item to make it purchasable.
                Open a reason to see its items.
              </p>
            </div>
            <div className="border-border overflow-x-auto rounded-md border">
              <Table className="text-xs" aria-label="Offered but not purchasable, by reason">
                <TableHeader>
                  <TableRow>
                    <TableHead>Reason</TableHead>
                    <TableHead>Effect on customers</TableHead>
                    <TableHead>What it means and how to fix it</TableHead>
                    <TableHead className="text-end">Items</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reasons.map(([reason, count]) => {
                    const open = expanded.has(reason);
                    return (
                      <Fragment key={reason}>
                        <TableRow className={open ? 'bg-muted/40 border-b-0' : undefined}>
                          <TableCell className="font-medium whitespace-nowrap">
                            <button
                              type="button"
                              className="hover:text-primary inline-flex items-center gap-1 text-start"
                              aria-expanded={open}
                              aria-controls={`health-items-${reason}`}
                              onClick={() => toggle(reason)}
                            >
                              {open ? (
                                <ChevronDown className="size-3.5" aria-hidden="true" />
                              ) : (
                                <ChevronRight className="size-3.5" aria-hidden="true" />
                              )}
                              {reasonLabel(reason)}
                            </button>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {HIDING_REASONS.has(reason) ? (
                              <StatusBadge variant="neutral">Hidden from storefront</StatusBadge>
                            ) : (
                              <StatusBadge variant="warning">Shown, cannot buy</StatusBadge>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground whitespace-normal">
                            {REASON_HELP[reason] ?? '—'}
                          </TableCell>
                          <TableCell className="text-end font-medium tabular-nums">
                            {formatNumber(count)}
                          </TableCell>
                        </TableRow>
                        {open && (
                          <TableRow
                            id={`health-items-${reason}`}
                            className="bg-muted/40 hover:bg-muted/40"
                          >
                            <TableCell colSpan={4} className="pt-0 whitespace-normal">
                              <ReasonItems countryId={countryId} reason={reason} />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </section>

      {canPricing && (
        <section className="flex flex-col gap-2">
          <div>
            <h3 className="text-sm font-medium">Pricing, last 30 days</h3>
            <p className="text-muted-foreground">
              How completed orders in this country were priced against the pricing parameters, per
              brand ({from} to {to}).
            </p>
          </div>
          {pricing.isLoading && <Skeleton className="h-24" />}
          {pricing.error && (
            <ErrorState error={pricing.error} onRetry={() => void pricing.refetch()} />
          )}
          {pricing.data && <PricingSummary report={pricing.data} />}
        </section>
      )}
    </div>
  );
}

/** The items one reason holds back, loaded when the reason is opened. */
function ReasonItems({ countryId, reason }: { countryId: number; reason: string }) {
  const items = useCatalogueHealthItems(countryId, reason, true);

  if (items.isLoading) return <Skeleton className="h-16" />;
  if (items.error) return <ErrorState error={items.error} onRetry={() => void items.refetch()} />;
  const rows = items.data ?? [];
  if (rows.length === 0)
    return <p className="text-muted-foreground py-2">No items are held back by this any more.</p>;

  return (
    <div className="border-border bg-background max-h-96 overflow-auto rounded-md border">
      <Table className="text-xs" aria-label={`Items held back: ${reasonLabel(reason)}`}>
        <TableHeader>
          <TableRow>
            <TableHead>Brand</TableHead>
            <TableHead>Denomination</TableHead>
            <TableHead>Region</TableHead>
            <TableHead>Customers</TableHead>
            <TableHead>Also held back by</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((item) => {
            const others = [
              ...item.state.visibilityReasons,
              ...item.state.sellabilityReasons,
            ].filter((other) => other !== reason && other !== 'NotVisible');
            return (
              <TableRow key={item.denominationId}>
                <TableCell>
                  <Link
                    to={`/brands/${item.brandId}`}
                    className="text-primary font-medium underline-offset-2 hover:underline"
                  >
                    {item.brandName}
                  </Link>
                </TableCell>
                <TableCell>
                  {item.name}
                  {!item.isActive && (
                    <span className="ms-1">
                      <StatusBadge variant="neutral">Inactive</StatusBadge>
                    </span>
                  )}
                </TableCell>
                <TableCell>{item.regionName ?? '—'}</TableCell>
                <TableCell className="whitespace-nowrap">
                  {item.state.visible ? 'Shown, cannot buy' : 'Hidden'}
                </TableCell>
                <TableCell className="whitespace-normal">
                  {others.length === 0 ? (
                    <span className="text-muted-foreground">Nothing else</span>
                  ) : (
                    others.map(reasonLabel).join(', ')
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function FunnelStep({
  icon,
  title,
  value,
  ratio,
  explanation,
  footnote,
}: {
  icon: ReactNode;
  title: string;
  value: number;
  ratio: number;
  explanation: string;
  footnote: ReactNode;
}) {
  return (
    <div className="border-border flex flex-col gap-1.5 rounded-md border p-3">
      <div className="text-muted-foreground flex items-center gap-1.5 font-medium tracking-wide uppercase">
        {icon}
        {title}
      </div>
      <div className="text-2xl font-semibold tabular-nums">{formatNumber(value)}</div>
      <div
        className="bg-muted h-1.5 overflow-hidden rounded-full"
        role="presentation"
        aria-hidden="true"
      >
        <div className="bg-primary h-full rounded-full" style={{ width: `${ratio * 100}%` }} />
      </div>
      <div className="text-muted-foreground">{footnote}</div>
      <p className="text-muted-foreground mt-auto border-t pt-1.5">{explanation}</p>
    </div>
  );
}

function FunnelArrow() {
  return (
    <div className="text-muted-foreground hidden items-center md:flex" aria-hidden="true">
      <ArrowRight className="size-4" />
    </div>
  );
}

const COLUMNS: { label: string; help: string }[] = [
  { label: 'Priced orders', help: 'Completed orders that carry a dynamic-pricing snapshot.' },
  { label: 'Avg margin', help: 'Average margin actually earned over cost.' },
  { label: 'Avg target', help: 'Average target margin the parameters aimed for.' },
  { label: 'Below target', help: 'Orders whose margin came in under the target.' },
  { label: 'At/near floor', help: 'Orders priced at or just above the lowest allowed margin.' },
  { label: 'At ceiling', help: 'Orders capped by the highest allowed margin.' },
  { label: 'Guardrail', help: 'Orders where the floor or ceiling changed the calculated price.' },
];

const pct = (value: number | null) => (value === null ? '—' : `${value}%`);
const num = 'text-end tabular-nums';

function PricingSummary({ report }: { report: PricingHealth }) {
  if (report.rows.length === 0)
    return (
      <p className="text-muted-foreground">
        No priced sales in this period.
        {report.unpricedOrders > 0 &&
          ` ${formatNumber(report.unpricedOrders)} completed order(s) predate dynamic pricing.`}
      </p>
    );

  const totalOrders = report.rows.reduce((sum, row) => sum + row.pricedOrders, 0);
  const totalBelow = report.rows.reduce((sum, row) => sum + row.belowTargetOrders, 0);
  const totalGuardrail = report.rows.reduce((sum, row) => sum + row.guardrailOrders, 0);

  return (
    <>
      <p>
        <span className="font-medium">{formatNumber(totalOrders)}</span> priced orders ·{' '}
        <span className={totalBelow > 0 ? 'text-status-warning font-medium' : 'font-medium'}>
          {formatNumber(totalBelow)}
        </span>{' '}
        below target ({share(totalBelow, totalOrders)}) ·{' '}
        <span className="font-medium">{formatNumber(totalGuardrail)}</span> hit a guardrail
      </p>
      <div className="border-border overflow-x-auto rounded-md border">
        <Table className="text-xs" aria-label="Pricing by brand, last 30 days">
          <TableHeader>
            <TableRow>
              <TableHead>Brand</TableHead>
              <TableHead>Currency</TableHead>
              {COLUMNS.map((column) => (
                <TableHead key={column.label} className="text-end" title={column.help}>
                  {column.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.rows.map((row) => (
              <TableRow key={`${row.key}-${row.currencyCode}`}>
                <TableCell className="font-medium">{row.label}</TableCell>
                <TableCell>{row.currencyCode ?? '—'}</TableCell>
                <TableCell className={num}>{formatNumber(row.pricedOrders)}</TableCell>
                <TableCell className={num}>{pct(row.avgRealizedMarginPct)}</TableCell>
                <TableCell className={num}>{pct(row.avgTargetMarginPct)}</TableCell>
                <TableCell className={num}>
                  {row.belowTargetOrders > 0 ? (
                    <StatusBadge variant="warning">
                      {formatNumber(row.belowTargetOrders)}
                    </StatusBadge>
                  ) : (
                    0
                  )}
                </TableCell>
                <TableCell className={num}>{formatNumber(row.atNearFloorOrders)}</TableCell>
                <TableCell className={num}>{formatNumber(row.atCeilingOrders)}</TableCell>
                <TableCell className={num}>{formatNumber(row.guardrailOrders)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <dl className="text-muted-foreground grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-2">
        {COLUMNS.map((column) => (
          <div key={column.label}>
            <dt className="text-foreground inline font-medium">{column.label}:</dt>{' '}
            <dd className="inline">{column.help}</dd>
          </div>
        ))}
      </dl>
      {report.unpricedOrders > 0 && (
        <p className="text-muted-foreground">
          {formatNumber(report.unpricedOrders)} completed order(s) were priced before dynamic
          pricing and are not included.
        </p>
      )}
    </>
  );
}
